import { useLayoutEffect, useRef, type ComponentType } from 'react';
import { useLinkComponent, type NavLinkComponentProps } from '../navigation';
import styles from './TabNav.module.css';

export interface TabNavItem {
  key: string;
  label: string;
  href: string;
  active?: boolean;
}

export interface TabNavProps {
  label: string;
  items: TabNavItem[];
  linkComponent?: ComponentType<NavLinkComponentProps>;
}

/**
 * Text tabs that switch between pages of one area (links with
 * `aria-current`), e.g. Völker · Behandlungen · Fütterungen. The underline
 * glides to the active tab; on narrow screens the row scrolls horizontally,
 * keeps the active tab in view and fades out where more tabs are hidden.
 */
export function TabNav({ label, items, linkComponent }: TabNavProps) {
  const Link = useLinkComponent(linkComponent);
  const navRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const activeKey = items.find((i) => i.active)?.key;

  useLayoutEffect(() => {
    const nav = navRef.current;
    const list = listRef.current;
    if (!nav || !list) return;
    const place = () => {
      const active = list.querySelector<HTMLElement>('[aria-current="page"]');
      if (!active) {
        list.style.setProperty('--indicator-opacity', '0');
        return;
      }
      list.style.setProperty('--indicator-x', `${active.offsetLeft}px`);
      list.style.setProperty('--indicator-width', `${active.offsetWidth}px`);
      list.style.setProperty('--indicator-opacity', '1');
      const overflow = active.offsetLeft + active.offsetWidth - (nav.scrollLeft + nav.clientWidth);
      if (overflow > 0 || active.offsetLeft < nav.scrollLeft) {
        nav.scrollLeft = Math.max(0, active.offsetLeft - 16);
      }
      fade();
    };
    // Fades the edge where more tabs are hidden (#60).
    const fade = () => {
      nav.toggleAttribute('data-more-start', nav.scrollLeft > 1);
      nav.toggleAttribute('data-more-end', nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1);
    };
    place();
    nav.addEventListener('scroll', fade, { passive: true });
    if (typeof ResizeObserver === 'undefined') {
      return () => nav.removeEventListener('scroll', fade);
    }
    const observer = new ResizeObserver(place);
    observer.observe(list);
    observer.observe(nav);
    return () => {
      observer.disconnect();
      nav.removeEventListener('scroll', fade);
    };
  }, [activeKey, items.length]);

  return (
    <nav aria-label={label} className={styles.nav} ref={navRef}>
      <ul className={styles.list} ref={listRef}>
        <li className={styles.indicator} aria-hidden="true" />
        {items.map((item) => (
          <li key={item.key}>
            <Link
              href={item.href}
              className={[styles.tab, item.active && styles.active].filter(Boolean).join(' ')}
              {...(item.active ? { 'aria-current': 'page' as const } : {})}
            >
              <span className={styles.label} data-label={item.label}>
                {item.label}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
